import ButtonT from '@components/tailwind-components/Button/ButtonT';
import InputWithLabelT from '@components/tailwind-components/Input/InputWithLabelT';
import SelectInputWithLabelT from '@components/tailwind-components/Input/SelectInputWithLabelT';
import { toastSuccessTranslated } from '@helpers/toast';
import { useMainSelector } from '@hooks/useMainSelector';
import type React from 'react';
import { useCallback, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useFlowActions } from '../../../../hooks/flow/flow/useFlowActions';
import { useModal } from '../../../../hooks/modal/useModal';

interface Props {
  currentSubFlowName: string;
  currentRevisionId?: string;
}

interface ModalDataProps {
  name: string;
  revisionId?: string;
}

export const EditSubFlowForm: React.FC<Props> = ({
  currentSubFlowName,
  currentRevisionId,
}) => {
  const { updateSubFlow } = useFlowActions();
  const { hideModal } = useModal();
  const { t } = useTranslation();
  const {
    designer: {
      revisions,
      mainRevisionDetail,
      flow: { subFlows },
    },
  } = useMainSelector();

  const { handleSubmit, control } = useForm<ModalDataProps>({
    defaultValues: {
      name: currentSubFlowName,
      revisionId: currentRevisionId ?? mainRevisionDetail?._id,
    },
    shouldUseNativeValidation: true,
  });
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const hideAndSubmit = useCallback(
    (data: ModalDataProps) => {
      const { name, revisionId } = data;

      // Backward compatibility.
      // If the current revision is not set and we are on the main revision, then we can set the current revision to the main revision.
      if (!currentRevisionId && mainRevisionDetail?.isMainRevision) {
        currentRevisionId = mainRevisionDetail._id;
      }

      // * If the name of the subflow and revision is not changed, then just close the modal without any action
      if (name === currentSubFlowName && revisionId === currentRevisionId) {
        hideModal();
        return;
      }

      const subFlowExists = subFlows.find(
        (subFlow) => subFlow === name && name !== currentSubFlowName,
      );
      // * Check if entered subflow name is already exists or not.
      if (subFlowExists) {
        setErrorMessage(t('flow.sub-flow.name.exists'));
        return;
      }

      setErrorMessage(null);

      // * If the name of the subflow or revision is changed, then delete the old subflow
      if (name !== currentSubFlowName || revisionId !== currentRevisionId) {
        updateSubFlow(currentSubFlowName, name, currentRevisionId, revisionId);
      }
      hideModal();
      toastSuccessTranslated('flow.sub-flow.changes.toast.success');
    },
    [
      hideModal,
      subFlows,
      t,
      currentSubFlowName,
      currentRevisionId,
      updateSubFlow,
      mainRevisionDetail,
    ],
  );

  return (
    <form
      className='tailwind-modal-form'
      onSubmit={handleSubmit(hideAndSubmit)}>
      <div className='tailwind-modal-body'>
        <p className='text-sm mb-2 italic'>
          {t('flow.sub-flow.edit.description')}
        </p>
        <div className='grid grid-cols-1 gap-x-6 gap-y-2'>
          <div className='col-span-1 relative mb-3'>
            {errorMessage && (
              <p className='text-red-base text-tiny absolute bottom-[-11px] left-0'>
                {errorMessage}
              </p>
            )}

            <InputWithLabelT
              type='text'
              field='name'
              placeholder={t('flow.sub-flow.name.field')}
              label={t('flow.sub-flow.name.field')}
              id='name'
              control={control}
              inputClassName={errorMessage ? '!ring-red-base' : ''}
              options={{
                required: t('flow.sub-flow.name.required'),
                onChange: () => {
                  if (errorMessage) {
                    setErrorMessage(null);
                  }
                },
              }}
            />
          </div>
          <div className='col-span-1'>
            <SelectInputWithLabelT
              field='revisionId'
              label={t('flow.sub-flow.revision.field')}
              control={control}
              values={(revisions || []).map((revision) => ({
                value: revision._id.toString(),
                description: revision.name,
              }))}
            />
          </div>
        </div>
      </div>
      <div className='tailwind-modal-footer'>
        <ButtonT type='submit' styling='primary' size='xs' rounding='md'>
          {t('general.buttons.save')}
        </ButtonT>
      </div>
    </form>
  );
};
